import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { usePrefersCoarsePointer } from '../../utils/usePrefersCoarsePointer';
import { getLinkDisplayTitle } from '../../utils/fileUtils';
import { isOverSharedDragGrip } from '../../utils/sharedFilesDrag';

const SHOW_DELAY_MS = 220;
const HIDE_DELAY_MS = 120;
const PREVIEW_WIDTH = 320;
const PREVIEW_GAP = 14;

const LinkHoverPreviewContent = ({
  link,
  href,
  canRename,
  isEditing,
  editName,
  onEditNameChange,
  onStartEdit,
  onCancelEdit,
  onSaveRename,
  isSaving,
}) => {
  const displayTitle = getLinkDisplayTitle(link);

  return (
    <div className="flex flex-col">
      <div className="bg-gray-50 dark:bg-gray-900 flex items-center justify-center min-h-[120px] max-h-[200px] overflow-hidden">
        {link.imageUrl ? (
          <img
            src={link.imageUrl}
            alt=""
            className="max-w-[320px] max-h-[200px] w-full object-cover"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        ) : (
          <svg className="w-16 h-16 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
          </svg>
        )}
      </div>

      <div className="p-3 border-t border-gray-100 dark:border-gray-700">
        {isEditing ? (
          <div className="flex items-center gap-1">
            <div className="flex flex-1 min-w-0 items-center border border-primary-500 rounded overflow-hidden bg-white dark:bg-gray-800">
              <input
                type="text"
                value={editName}
                onChange={(e) => onEditNameChange(e.target.value)}
                className="flex-1 min-w-0 px-2 py-1 text-xs focus:outline-none bg-transparent text-gray-900 dark:text-white"
                autoFocus
                disabled={isSaving}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') onSaveRename();
                  if (e.key === 'Escape') onCancelEdit();
                }}
              />
            </div>
            <button
              type="button"
              onClick={onSaveRename}
              disabled={isSaving}
              className="p-1 text-green-600 hover:text-green-700 disabled:opacity-50"
              title="Save name"
              aria-label="Save link name"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </button>
            <button
              type="button"
              onClick={onCancelEdit}
              disabled={isSaving}
              className="p-1 text-red-600 hover:text-red-700 disabled:opacity-50"
              title="Cancel"
              aria-label="Cancel rename"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-1 min-w-0">
            <h4 className="flex-1 min-w-0 text-xs font-medium text-gray-900 dark:text-white truncate" title={displayTitle}>
              {displayTitle}
            </h4>
            {canRename && (
              <button
                type="button"
                onClick={onStartEdit}
                className="flex-shrink-0 p-1 rounded text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                title="Rename link"
                aria-label={`Rename ${displayTitle}`}
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              </button>
            )}
          </div>
        )}
        <p className="text-[10px] text-primary-600 dark:text-primary-400 truncate mt-0.5" title={href}>
          {link.siteName || link.domain || href}
        </p>
        {link.description && !isEditing && (
          <p className="text-[10px] text-gray-500 dark:text-gray-400 line-clamp-2 mt-1">
            {link.description}
          </p>
        )}
      </div>
    </div>
  );
};

const LinkHoverPreview = ({ link, href, children, className = '', canRename = false, onRename }) => {
  const coarsePointer = usePrefersCoarsePointer();
  const anchorRef = useRef(null);
  const showTimerRef = useRef(null);
  const hideTimerRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const clearTimers = useCallback(() => {
    if (showTimerRef.current) {
      clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  const updatePosition = useCallback(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const viewportW = window.innerWidth;
    const viewportH = window.innerHeight;
    const previewH = 320;

    let left = rect.right + PREVIEW_GAP;
    if (left + PREVIEW_WIDTH > viewportW - 8) {
      left = rect.left - PREVIEW_WIDTH - PREVIEW_GAP;
    }
    if (left < 8) {
      left = Math.max(8, (viewportW - PREVIEW_WIDTH) / 2);
    }

    let top = rect.top;
    if (top + previewH > viewportH - 8) {
      top = Math.max(8, viewportH - previewH - 8);
    }

    setPosition({ top, left });
  }, []);

  const scheduleShow = useCallback(() => {
    if (coarsePointer) return;
    clearTimers();
    showTimerRef.current = setTimeout(() => {
      updatePosition();
      setVisible(true);
    }, SHOW_DELAY_MS);
  }, [clearTimers, coarsePointer, updatePosition]);

  const scheduleHide = useCallback(() => {
    if (isEditing || isSaving) return;
    clearTimers();
    hideTimerRef.current = setTimeout(() => {
      setVisible(false);
    }, HIDE_DELAY_MS);
  }, [clearTimers, isEditing, isSaving]);

  const hideImmediately = useCallback(() => {
    clearTimers();
    setVisible(false);
  }, [clearTimers]);

  const handleAnchorMouseOverCapture = useCallback((e) => {
    if (isOverSharedDragGrip(e)) {
      hideImmediately();
    }
  }, [hideImmediately]);

  const handleAnchorMouseEnter = useCallback((e) => {
    if (isOverSharedDragGrip(e)) {
      hideImmediately();
      return;
    }
    scheduleShow();
  }, [hideImmediately, scheduleShow]);

  const handleAnchorMouseMove = useCallback((e) => {
    if (isOverSharedDragGrip(e)) {
      hideImmediately();
      return;
    }
    if (!visible && !showTimerRef.current) {
      scheduleShow();
    }
  }, [hideImmediately, scheduleShow, visible]);

  const cancelHide = useCallback(() => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!visible) return undefined;
    const onScrollOrResize = () => updatePosition();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [visible, updatePosition]);

  useEffect(() => () => clearTimers(), [clearTimers]);

  useEffect(() => {
    if (!visible) {
      setIsEditing(false);
      setEditName('');
      setIsSaving(false);
    }
  }, [visible]);

  const handleStartEdit = useCallback(() => {
    setEditName(getLinkDisplayTitle(link));
    setIsEditing(true);
    cancelHide();
  }, [link, cancelHide]);

  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
    setEditName('');
  }, []);

  const handleSaveRename = useCallback(async () => {
    const trimmed = editName.trim();
    if (!trimmed || !onRename) {
      handleCancelEdit();
      return;
    }
    if (trimmed === getLinkDisplayTitle(link)) {
      handleCancelEdit();
      return;
    }
    setIsSaving(true);
    try {
      await onRename(link, trimmed);
      handleCancelEdit();
    } catch {
      // Parent handles error notification
    } finally {
      setIsSaving(false);
    }
  }, [editName, handleCancelEdit, link, onRename]);

  if (coarsePointer) {
    return <div className={className}>{children}</div>;
  }

  return (
    <>
      <div
        ref={anchorRef}
        className={className}
        onMouseEnter={handleAnchorMouseEnter}
        onMouseMove={handleAnchorMouseMove}
        onMouseOverCapture={handleAnchorMouseOverCapture}
        onMouseLeave={scheduleHide}
      >
        {children}
      </div>

      {visible && ReactDOM.createPortal(
        <div
          className="fixed z-[9990] w-[320px] rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-2xl overflow-hidden pointer-events-auto"
          style={{ top: position.top, left: position.left }}
          onMouseEnter={cancelHide}
          onMouseLeave={scheduleHide}
          role="tooltip"
          aria-label={`Preview of ${getLinkDisplayTitle(link)}`}
        >
          <LinkHoverPreviewContent
            link={link}
            href={href}
            canRename={canRename && !!onRename}
            isEditing={isEditing}
            editName={editName}
            onEditNameChange={setEditName}
            onStartEdit={handleStartEdit}
            onCancelEdit={handleCancelEdit}
            onSaveRename={handleSaveRename}
            isSaving={isSaving}
          />
        </div>,
        document.body
      )}
    </>
  );
};

export default LinkHoverPreview;
